import { Button } from "@/components/ui/button";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { ExternalAccountsAPI } from "@/features/external-accounts/api/external-accounts";
import type {
  ExternalAccount,
  ExternalAccountSettings,
} from "@/features/external-accounts/types";
import { useTranslation } from "@/features/utils/i18n";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { SyncSettingsDialog } from "./sync-settings-dialog";

interface AccountWithSettings extends ExternalAccount {
  settings?: ExternalAccountSettings;
}

export interface GlobalSyncSettingsHandle {
  openDialog: () => void;
}

export const GlobalSyncSettings = forwardRef<GlobalSyncSettingsHandle>(
  (_props, ref) => {
    const { t } = useTranslation("externalAccounts");
    const client = useCurrentClient();
    const clientId = client?.client?.id;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [, setAccounts] = useState<AccountWithSettings[]>([]);

    const loadAccounts = async () => {
      if (!clientId) return;

      try {
        const allAccounts = await ExternalAccountsAPI.getAccounts(clientId);

        // Load settings for each account in parallel
        const accountsWithData = await Promise.all(
          allAccounts.map(async (account) => {
            try {
              const settings = await ExternalAccountsAPI.getSettings(
                clientId,
              ).catch(() => null);

              return {
                ...account,
                settings: settings || undefined,
              };
            } catch {
              return account;
            }
          }),
        );

        setAccounts(accountsWithData);
      } catch (error) {
        console.error("Error loading accounts:", error);
      }
    };

    useEffect(() => {
      if (clientId) {
        loadAccounts();
      }
    }, [clientId]);

    useImperativeHandle(ref, () => ({
      openDialog: () => setDialogOpen(true),
    }));

    return (
      <>
        <Button variant="outline" onClick={() => setDialogOpen(true)}>
          {t("externalAccounts.buttons.synchronization")}
        </Button>

        {clientId && (
          <SyncSettingsDialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                // Reload summary when dialog closes
                loadAccounts();
              }
            }}
            clientId={clientId}
          />
        )}
      </>
    );
  },
);

GlobalSyncSettings.displayName = "GlobalSyncSettings";
